import {Ref, useEffect, useLayoutEffect, useMemo} from 'react';
import {DetailedProps, join, setRef} from '@esgi/ui-kit/utils';
import {distinctUntilChanged} from 'rxjs';
import {map} from 'rxjs/operators';
import {Portal} from '../portal';
import {LayerManager} from './layer-manager';

import styles from './layer.module.less';

export type LayerProps = {
	isTop?: Ref<boolean>;
	zIndex?: number;
	inactive?: boolean;
	ref?: Ref<HTMLDivElement>;
	preventStackOver?: boolean;
	owner?: HTMLElement
} & Omit<DetailedProps<HTMLDivElement>, 'style'>

export function Layer({className, zIndex, inactive, children, isTop, owner, preventStackOver, ...props}: LayerProps): JSX.Element {
	const registerInfo = useMemo(() => {
		return LayerManager.register({
			desirePriority: zIndex,
			active: !inactive,
			preventStackOver,
		});
	}, []);

	useEffect(() => inactive ? registerInfo?.deactivate() : registerInfo?.activate(), [inactive]);
	useEffect(() => () => LayerManager.deregister(registerInfo), []);
	useLayoutEffect(() => {
		const sub = LayerManager.currentActive$.pipe(
			map(active => active === registerInfo),
			distinctUntilChanged(),
		).subscribe(isActive => setRef(isActive, isTop));
		return () => sub.unsubscribe();
	}, [registerInfo, isTop]);

	return <Portal owner={owner ?? document.body}>
		<div {...props} className={join(styles.layer, className)}
		     style={{zIndex: registerInfo?.priority || LayerManager.defaultPriority}}>
			{children}
		</div>
	</Portal>;
}
